import { useState, useEffect, useRef } from 'react';

interface Message {
  id: string;
  userId: string;
  username: string;
  message: string;
  response?: string;
  timestamp: number;
  status: 'pending' | 'answered';
  type?: 'normal' | 'activation_request';
  screenshotProof?: string;
}

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}

function lsSet(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

export default function ServiceClient() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showActivationForm, setShowActivationForm] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState<string>('');
  const [uploadError, setUploadError] = useState('');
  const [activationMessage, setActivationMessage] = useState('');

  const loadMessages = (user: any) => {
    if (!user?.phone) return;
    const all = lsGet<Message[]>('serviceClientMessages', []);
    const mine = all
      .filter(m => m.userId === user.phone)
      .sort((a, b) => a.timestamp - b.timestamp);
    setMessages(mine);
  };

  useEffect(() => {
    const user = lsGet<any>('currentUser', {});
    setCurrentUser(user);
    loadMessages(user);

    // Polling toutes les 5 secondes pour voir les réponses admin
    const interval = setInterval(() => {
      const freshUser = lsGet<any>('currentUser', {});
      loadMessages(freshUser);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Veuillez sélectionner une image valide'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError("L'image ne doit pas dépasser 5 MB"); return; }
    setUploadError('');
    const reader = new FileReader();
    reader.onloadend = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const sendActivationRequest = () => {
    if (!screenshotPreview || !currentUser) {
      setUploadError('Veuillez télécharger une capture d\'écran de votre paiement');
      return;
    }
    setSending(true);
    const msg: Message = {
      id: Date.now().toString(),
      userId: currentUser.phone,
      username: currentUser.username || currentUser.name || 'Utilisateur',
      message: activationMessage.trim() || 'Demande d\'activation manuelle de compte - Paiement effectué',
      timestamp: Date.now(),
      status: 'pending',
      type: 'activation_request',
      screenshotProof: screenshotPreview,
    };
    const all = lsGet<Message[]>('serviceClientMessages', []);
    all.push(msg);
    lsSet('serviceClientMessages', all);
    loadMessages(currentUser);
    setActivationMessage('');
    setScreenshotPreview('');
    setShowActivationForm(false);
    setSending(false);
    setSuccessMsg('Demande d\'activation envoyée ! L\'administration va vérifier votre paiement.');
    setTimeout(() => setSuccessMsg(''), 5000);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !currentUser) return;
    setSending(true);
    const msg: Message = {
      id: Date.now().toString(),
      userId: currentUser.phone,
      username: currentUser.username || currentUser.name || 'Utilisateur',
      message: newMessage.trim(),
      timestamp: Date.now(),
      status: 'pending',
      type: 'normal',
    };
    const all = lsGet<Message[]>('serviceClientMessages', []);
    all.push(msg);
    lsSet('serviceClientMessages', all);
    loadMessages(currentUser);
    setNewMessage('');
    setSending(false);
    setSuccessMsg("Message envoyé ! L'administration vous répondra bientôt.");
    setTimeout(() => setSuccessMsg(''), 4000);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const pendingCount = messages.filter(m => m.status === 'pending').length;
  const answeredCount = messages.filter(m => m.status === 'answered').length;

  return (
    <div className="space-y-6">
      {/* Logo + Contacts rapides */}
      <div className="bg-white rounded-xl p-5 shadow flex flex-col items-center justify-center gap-4">
        <img
          src="https://public.readdy.ai/ai/img_res/b4d20ed5521428732d3fc218d310c3b8.jpg"
          alt="SUCCESSLINK AGENCY Logo"
          className="h-20 object-contain"
        />
        <div className="flex flex-wrap gap-3 justify-center">
          <a href="https://whatsapp.com/channel/0029VbCEuEA1NCrMAsPILy3J" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 transition text-sm font-semibold whitespace-nowrap shadow cursor-pointer">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-lg"></i></div>
            WhatsApp
          </a>
          <a href="https://t.me/+RLed8IfVgMINDg0" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white rounded-xl hover:bg-sky-600 transition text-sm font-semibold whitespace-nowrap shadow cursor-pointer">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-telegram-line text-lg"></i></div>
            Telegram
          </a>
        </div>
      </div>

      {/* Bouton Demande d'activation manuelle */}
      {currentUser?.accountStatus !== 'active' && !showActivationForm && (
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Problème de paiement ?</h3>
              <p className="text-orange-100 text-sm mb-3">
                Si vous avez effectué votre paiement mais votre compte n'est pas encore activé, envoyez-nous la capture d'écran pour une activation manuelle.
              </p>
              <button onClick={() => setShowActivationForm(true)}
                className="px-5 py-2.5 bg-white text-orange-600 rounded-lg font-semibold text-sm hover:bg-orange-50 transition cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-upload-cloud-line text-lg"></i>
                Demander l'activation manuelle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire d'activation manuelle */}
      {showActivationForm && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-orange-200">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 text-white">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <i className="ri-file-upload-line text-xl"></i>
              Demande d'activation manuelle
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">📸 Capture d'écran du paiement *</label>
              <input type="file" accept="image/*" onChange={handleScreenshotChange} className="hidden" id="activation-screenshot-upload" />
              <label htmlFor="activation-screenshot-upload"
                className={`w-full px-4 py-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition block ${
                  uploadError ? 'border-red-400 bg-red-50' : screenshotPreview ? 'border-orange-400 bg-orange-50' : 'border-gray-300 hover:border-orange-400'
                }`}>
                {screenshotPreview ? (
                  <div className="space-y-2">
                    <img src={screenshotPreview} alt="Preview" className="max-h-40 mx-auto rounded-lg border border-gray-200" />
                    <p className="text-xs text-orange-700 font-semibold">✅ Capture chargée — Cliquez pour changer</p>
                  </div>
                ) : (
                  <div>
                    <i className="ri-image-add-line text-4xl text-gray-400 mb-2 block"></i>
                    <p className="text-sm text-gray-600 font-medium">Cliquez pour télécharger une image</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, JPEG (max 5 MB)</p>
                  </div>
                )}
              </label>
              {uploadError && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i> {uploadError}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Message (optionnel)</label>
              <textarea value={activationMessage} onChange={e => setActivationMessage(e.target.value)}
                placeholder="Méthode utilisée, montant, date..." rows={3} maxLength={500}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none resize-none" />
              <p className="text-xs text-gray-400 mt-1 text-right">{activationMessage.length}/500</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowActivationForm(false); setScreenshotPreview(''); setActivationMessage(''); setUploadError(''); }}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-sm transition cursor-pointer whitespace-nowrap">
                Annuler
              </button>
              <button onClick={sendActivationRequest} disabled={!screenshotPreview || sending}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-lg text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <i className="ri-send-plane-fill"></i>
                Envoyer la demande
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 text-white shadow">
        <h2 className="text-2xl font-bold mb-1">Service Client</h2>
        <p className="text-teal-100 text-sm">Posez vos questions, nous sommes là pour vous aider</p>
        <div className="flex gap-4 mt-4">
          <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
            <p className="text-xl font-bold">{messages.length}</p>
            <p className="text-xs text-teal-100">Total</p>
          </div>
          <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
            <p className="text-xl font-bold text-yellow-300">{pendingCount}</p>
            <p className="text-xs text-teal-100">En attente</p>
          </div>
          <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
            <p className="text-xl font-bold text-green-300">{answeredCount}</p>
            <p className="text-xs text-teal-100">Répondus</p>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <i className="ri-chat-history-line text-teal-600"></i>
          <h3 className="font-bold text-gray-800">Conversation avec l'administration</h3>
          <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
            <span className="w-2 h-2 bg-green-400 rounded-full inline-block animate-pulse"></span>
            En ligne
          </span>
        </div>

        <div className="p-4 max-h-[420px] overflow-y-auto space-y-4 bg-gray-50">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-message-3-line text-5xl text-gray-200 mb-3 block"></i>
              <p className="text-gray-400 text-sm">Aucun message pour le moment.<br />Envoyez votre première question ci-dessous.</p>
            </div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className="space-y-3">
                {/* Message utilisateur */}
                <div className="flex justify-end">
                  <div className="max-w-[80%]">
                    {msg.type === 'activation_request' && (
                      <div className="mb-2 px-3 py-1 bg-orange-100 border border-orange-300 rounded-full text-orange-700 text-xs font-bold inline-flex items-center gap-1">
                        <i className="ri-file-upload-line"></i> Demande d'activation
                      </div>
                    )}
                    <div className="bg-teal-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                      <p className="text-sm">{msg.message}</p>
                      {msg.screenshotProof && (
                        <div className="mt-2 pt-2 border-t border-teal-500">
                          <img src={msg.screenshotProof} alt="Capture d'écran"
                            className="max-h-32 rounded-lg border border-teal-400 cursor-pointer hover:opacity-90 transition"
                            onClick={() => window.open(msg.screenshotProof, '_blank')} />
                          <p className="text-xs text-teal-200 mt-1 flex items-center gap-1">
                            <i className="ri-image-line"></i> Cliquez pour agrandir
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 text-right mt-1">{formatDate(msg.timestamp)}</p>
                  </div>
                </div>

                {/* Réponse admin */}
                {msg.response ? (
                  <div className="flex justify-start">
                    <div className="max-w-[80%]">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                          <i className="ri-customer-service-2-line text-orange-600 text-xs"></i>
                        </div>
                        <span className="text-xs font-bold text-orange-600">Administration</span>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                        <p className="text-sm text-gray-700">{msg.response}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-start">
                    <div className="bg-white border border-dashed border-gray-200 rounded-2xl rounded-tl-sm px-4 py-2 shadow-sm">
                      <p className="text-xs text-yellow-600 flex items-center gap-1">
                        <i className="ri-time-line"></i> En attente de réponse...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Zone d'envoi */}
        <div className="p-4 border-t border-gray-100 bg-white">
          {successMsg && (
            <div className="mb-3 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line"></i> {successMsg}
            </div>
          )}
          <div className="flex gap-3 items-center">
            <input ref={inputRef} type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress} placeholder="Écrivez votre message..." maxLength={500}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 outline-none" />
            <button onClick={sendMessage} disabled={!newMessage.trim() || sending}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-teal-600 text-white rounded-xl text-sm font-semibold hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[100px] cursor-pointer whitespace-nowrap">
              <i className="ri-send-plane-fill"></i>
              <span>Envoyer</span>
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-right">{newMessage.length}/500</p>
        </div>
      </div>

      {/* Contact rapide */}
      <div className="bg-teal-50 border border-teal-100 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-2xl text-teal-600 mt-0.5"></i>
          <div>
            <h4 className="font-bold text-gray-800 mb-1">Besoin d'aide immédiate ?</h4>
            <p className="text-sm text-gray-600">Rejoignez notre communauté via les boutons en haut de cette page pour obtenir de l'aide rapidement.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
